import React, { useState, useEffect, useCallback, useRef, memo } from "react";
import JSZip from "jszip";
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table";
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css";
import clientConfig from "../config";
import { getAuthHeaders } from "../utils/apiFetch";
import styles from "./AccidentReportTable.module.css";
import { MoonLoader } from "react-spinners";
import DownloadIcon from "../components/icons/DownloadIcon";
import DeleteIcon from "../components/icons/DeleteIcon";
import EditIcon from "../components/icons/EditIcon";
import EditAccidentReportModal from "../components/accident/EditAccidentReportModal";
import { useUser } from "../contexts/UserContext";
import useIsOperate from "../hooks/useIsOperate";
import useIsFreesbe from "../hooks/useIsFreesbe";
import useIsAdmin from "../hooks/useIsAdmin";
import useIsSuperAdmin from "../hooks/useIsSuperAdmin";
import Button from "../components/UiElements/Button";
import Dropdown, { type DropdownItem } from "../components/UiElements/Dropdown";
import Input from "../components/UiElements/Input";
import Checkbox from "../components/UiElements/Checkbox";
import ErrorMessage from "../components/UiElements/ErrorMessage";
import { tokens } from "../design-system/tokens";
import { useDraggingScroll } from "../hooks/useDraggingScroll";

// Search input component
const SearchInput = memo(
  ({
    searchQuery,
    setSearchQuery,
    searchActive,
    handleClearSearch,
    searchInputRef,
  }: {
    searchQuery: string;
    setSearchQuery: (v: string) => void;
    searchActive: boolean;
    handleClearSearch: () => void;
    searchInputRef: React.RefObject<HTMLInputElement | null>;
  }) => {
    return (
      <div className={styles.searchContainer}>
        <Input
          ref={searchInputRef}
          type="text"
          placeholder="חפש לפי שם נהג, תעודת זהות, טלפון, מספר רכב..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          color="outlineSecondary"
          size="medium"
          rtl
          width="100%"
          maxWidth={500}
        />
        {searchActive ? (
          <Button
            label="נקה"
            color="outlineSecondary"
            size="small"
            width="auto"
            onClick={handleClearSearch}
          />
        ) : null}
      </div>
    );
  }
);

interface AccidentReport {
  _id: string;
  file_name: string;
  file_url: string;
  file_size: number;
  event_driver_name: string;
  event_driver_id: string;
  event_driver_phone: string;
  insured_car_license: string;
  selected_company: string;
  email_to_send_pdf: string;
  insured_damage_description: string;
  acident_description: string;
  acident_date: string;
  deleted?: boolean;
  createdAt: string;
  updatedAt: string;
}

const ADMIN_EMAIL = "guy@activebranding.co.il";

const AccidentReportTable = () => {
  const { isAuthenticated } = useUser();
  const isOperate = useIsOperate();
  const isFreesbe = useIsFreesbe();
  const isAdminRole = useIsAdmin();
  const isSuperAdmin = useIsSuperAdmin();
  const isCompanyScoped =
    (isOperate || isFreesbe) && !isAdminRole && !isSuperAdmin;
  const canDeleteReports = isAuthenticated && !isCompanyScoped;
  const [currentUserEmail, setCurrentUserEmail] = useState<string | null>(null);
  const [editReport, setEditReport] = useState<AccidentReport | null>(null);
  const [reports, setReports] = useState<AccidentReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    show: boolean;
    ids: string[];
    fileName?: string;
    isBulk: boolean;
  }>({
    show: false,
    ids: [],
    fileName: "",
    isBulk: false,
  });
  const [selectedReports, setSelectedReports] = useState<string[]>([]);
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(
    null
  );
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchActive, setSearchActive] = useState(false);
  const [sortBy, setSortBy] = useState<"selected_company" | "createdAt">("createdAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [showDeleted, setShowDeleted] = useState(false);
  const [hardDeleteConfirm, setHardDeleteConfirm] = useState<{
    show: boolean;
    ids: string[];
    fileName?: string;
    isBulk: boolean;
  }>({ show: false, ids: [], fileName: "", isBulk: false });
  const [hardDeleting, setHardDeleting] = useState(false);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const tableScrollProps = useDraggingScroll();

  const toggleSortByCompany = () => {
    if (sortBy === "selected_company") {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy("selected_company");
      setSortDir("asc");
    }
  };

  const toggleSortByCreatedAt = () => {
    if (sortBy === "createdAt") {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy("createdAt");
      setSortDir("asc");
    }
  };

  // Format date to Hebrew format
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Load accident reports from API
  const loadReports = useCallback(
    async (pageNum: number = 1, append: boolean = false, includeDeleted: boolean = false) => {
      setLoading(true);
      setError(null);
      try {
        const apiUrl = `${clientConfig.backendUrl}/forms/accident-report`;
        const params = new URLSearchParams({
          page: pageNum.toString(),
          limit: "100",
          ...(includeDeleted ? { includeDeleted: "true" } : {}),
        });
        const url = `${apiUrl}?${params}`;
        console.log("Loading accident reports from:", url, "page:", pageNum);

        const response = await fetch(url, { headers: getAuthHeaders() });

        if (!response.ok) {
          const contentType = response.headers.get("content-type");
          let errorMessage = `HTTP error! status: ${response.status}`;

          if (contentType && contentType.includes("application/json")) {
            try {
              const errorData = await response.json();
              errorMessage = errorData.error || errorMessage;
            } catch (e) {
              // If JSON parsing fails, use default error message
            }
          } else {
            // If response is HTML (like error page), read as text
            const text = await response.text().catch(() => "");
            if (text.includes("<!doctype") || text.includes("<html")) {
              errorMessage = `השרת החזיר שגיאה. בדוק את כתובת השרת והנתיב.`;
            }
          }

          throw new Error(errorMessage);
        }

        const data = await response.json();
        console.log("Response data:", data);
        const { accidentReports: newReports, totalPages } = data;

        if (append) {
          setReports((prev) => [...prev, ...newReports]);
        } else {
          setReports(newReports || []);
        }

        setHasMore(pageNum < totalPages);
        setPage(pageNum);
      } catch (err: any) {
        console.error("Error loading accident reports:", err);
        setError(err?.message || "שגיאה בטעינת הנתונים");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Handle load more (infinite scroll)
  const handleLoadMore = useCallback(() => {
    if (!loading && hasMore) {
      loadReports(page + 1, true, showDeleted);
    }
  }, [loading, hasMore, page, loadReports, showDeleted]);

  // Handle file download (via bot proxy + admin JWT; never direct public car-show URL)
  const handleDownload = async (fileUrl: string, fileName: string) => {
    try {
      console.log("[accident-download] start", {
        fileName,
        pathLen: fileUrl ? String(fileUrl).length : 0,
      });
      const proxyUrl = `${clientConfig.backendUrl}/forms/accident-report/proxy-file?path=${encodeURIComponent(fileUrl)}`;
      const res = await fetch(proxyUrl, { headers: getAuthHeaders() });
      if (!res.ok) {
        const body = await res.text().catch(() => "");
        console.error("[accident-download] fail", {
          status: res.status,
          fileName,
          body: body.slice(0, 200),
        });
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      const blob = await res.blob();
      console.log("[accident-download] ok", {
        fileName,
        bytes: blob.size,
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (err) {
      console.error("[accident-download] error", err);
      alert("שגיאה בהורדת הקובץ. נסה שוב.");
    }
  };

  // Delete reports
  const deleteReports = useCallback(async (ids: string[]) => {
    try {
      setBulkDeleting(true);
      await Promise.all(
        ids.map(async (id) => {
          const response = await fetch(
            `${clientConfig.backendUrl}/forms/accident-report/${id}`,
            {
              method: "DELETE",
              headers: getAuthHeaders(),
            }
          );
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
        })
      );
      setReports((prev) => prev.filter((item) => !ids.includes(item._id)));
      setSelectedReports((prev) => prev.filter((id) => !ids.includes(id)));
      setDeleteConfirm({
        show: false,
        ids: [],
        fileName: "",
        isBulk: false,
      });
    } catch (err) {
      console.error("Error deleting accident report:", err);
      alert("שגיאה במחיקה. נסה שוב.");
    } finally {
      setBulkDeleting(false);
    }
  }, []);

  // Show delete confirmation
  const showDeleteConfirm = (id: string, fileName: string) => {
    setDeleteConfirm({
      show: true,
      ids: [id],
      fileName,
      isBulk: false,
    });
  };

  // Cancel delete
  const cancelDelete = () => {
    setDeleteConfirm({
      show: false,
      ids: [],
      fileName: "",
      isBulk: false,
    });
  };

  // Confirm delete
  const confirmDelete = () => {
    if (deleteConfirm.ids.length) {
      deleteReports(deleteConfirm.ids);
    }
  };

  // Hard delete (permanent) - admin only
  const hardDeleteReports = useCallback(async (ids: string[]) => {
    try {
      setHardDeleting(true);
      await Promise.all(
        ids.map(async (id) => {
          const response = await fetch(
            `${clientConfig.backendUrl}/forms/accident-report/${id}/hard`,
            { method: "DELETE", headers: getAuthHeaders() }
          );
          if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        })
      );
      setReports((prev) => prev.filter((item) => !ids.includes(item._id)));
      setSelectedReports((prev) => prev.filter((id) => !ids.includes(id)));
      setHardDeleteConfirm({ show: false, ids: [], fileName: "", isBulk: false });
    } catch (err) {
      console.error("Error hard deleting:", err);
      alert("שגיאה במחיקה לצמיתות. נסה שוב.");
    } finally {
      setHardDeleting(false);
    }
  }, []);

  const showHardDeleteConfirm = (id: string, fileName: string) => {
    setHardDeleteConfirm({ show: true, ids: [id], fileName, isBulk: false });
  };

  const confirmHardDelete = () => {
    if (hardDeleteConfirm.ids.length) {
      hardDeleteReports(hardDeleteConfirm.ids);
    }
  };

  const toggleSelectAll = (checked: boolean) => {
    const filteredReports = getFilteredReports();
    if (checked) {
      setSelectedReports(filteredReports.map((report) => report._id));
    } else {
      setSelectedReports([]);
    }
    setLastSelectedIndex(null);
  };

  const toggleSelectReport = (
    report: AccidentReport,
    index: number,
    shiftKey: boolean
  ) => {
    setSelectedReports((prev) => {
      let updated = [...prev];
      const isSelected = updated.includes(report._id);
      if (shiftKey && lastSelectedIndex !== null) {
        const [start, end] = [
          Math.min(lastSelectedIndex, index),
          Math.max(lastSelectedIndex, index),
        ];
        const rangeIds = reports
          .slice(start, end + 1)
          .map((item) => item._id);
        if (isSelected) {
          updated = updated.filter((id) => !rangeIds.includes(id));
        } else {
          rangeIds.forEach((id) => {
            if (!updated.includes(id)) {
              updated.push(id);
            }
          });
        }
      } else if (isSelected) {
        updated = updated.filter((id) => id !== report._id);
      } else {
        updated.push(report._id);
      }
      return updated;
    });
    setLastSelectedIndex(index);
  };

  const handleDeleteSelected = () => {
    if (!selectedReports.length) return;
    setDeleteConfirm({
      show: true,
      ids: selectedReports,
      isBulk: true,
      fileName: "",
    });
  };

  const handleHardDeleteSelected = () => {
    if (!selectedReports.length) return;
    setHardDeleteConfirm({
      show: true,
      ids: selectedReports,
      isBulk: true,
      fileName: "",
    });
  };

  const handleEditSaved = (updated: Partial<AccidentReport> & { _id: string }) => {
    setReports((prev) =>
      prev.map((r) => (r._id === updated._id ? { ...r, ...updated } : r))
    );
  };

  const handleDownloadSelected = async () => {
    if (!selectedReports.length) return;
    const toDownload = reports.filter((r) => selectedReports.includes(r._id));
    setBulkDownloading(true);
    try {
      console.log("[accident-download] bulk_start", { count: toDownload.length });
      const zip = new JSZip();
      const seenNames = new Map<string, number>();
      const proxyUrl = `${clientConfig.backendUrl}/forms/accident-report/proxy-file`;
      let okCount = 0;
      let failCount = 0;
      for (const report of toDownload) {
        const url = `${proxyUrl}?path=${encodeURIComponent(report.file_url)}`;
        const res = await fetch(url, { headers: getAuthHeaders() });
        if (!res.ok) {
          const body = await res.text().catch(() => "");
          failCount += 1;
          console.error("[accident-download] bulk_item_fail", {
            status: res.status,
            fileName: report.file_name,
            pathLen: report.file_url ? String(report.file_url).length : 0,
            body: body.slice(0, 200),
          });
          continue;
        }
        const blob = await res.blob();
        okCount += 1;
        console.log("[accident-download] bulk_item_ok", {
          fileName: report.file_name,
          bytes: blob.size,
        });
        let fileName = report.file_name || "file";
        const count = (seenNames.get(fileName) ?? 0) + 1;
        seenNames.set(fileName, count);
        if (count > 1) {
          const ext = fileName.includes(".") ? fileName.slice(fileName.lastIndexOf(".")) : "";
          const base = fileName.includes(".") ? fileName.slice(0, fileName.lastIndexOf(".")) : fileName;
          fileName = `${base}_${count}${ext}`;
        }
        zip.file(fileName, blob);
      }
      console.log("[accident-download] bulk_done", { okCount, failCount });
      const zipBlob = await zip.generateAsync({ type: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(zipBlob);
      link.download = `דוחות-תאונות-${new Date().toISOString().slice(0, 10)}.zip`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      console.error("[accident-download] bulk_error", err);
      alert("שגיאה ביצירת קובץ ZIP. נסה שוב.");
    } finally {
      setBulkDownloading(false);
    }
  };

  // Ref for load more trigger
  const loadMoreRef = React.useRef<HTMLDivElement>(null);

  // Clean up selected reports when reports change
  useEffect(() => {
    setSelectedReports((prev) =>
      prev.filter((id) => reports.some((report) => report._id === id))
    );
  }, [reports]);

  // Load initial data
  useEffect(() => {
    loadReports(1, false, showDeleted);
  }, [loadReports, showDeleted]);

  // Fetch current user email for admin edit check
  useEffect(() => {
    const fetchCurrentUser = async () => {
      const token = localStorage.getItem("token");
      if (!token) return;
      try {
        const res = await fetch(`${clientConfig.backendUrl}/users/validate-token`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await res.json();
        if (!data.userId) return;
        const usersRes = await fetch(
          `${clientConfig.backendUrl}/users/get-users?phone=`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const usersData = await usersRes.json();
        if (usersData.users?.length) {
          const found = usersData.users.find((u: any) => u._id === data.userId);
          if (found?.email) setCurrentUserEmail(found.email);
        }
      } catch {
        // ignore
      }
    };
    fetchCurrentUser();
  }, []);

  const isAdminUser = currentUserEmail === ADMIN_EMAIL;

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !searchActive) {
          handleLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
      observer.disconnect();
    };
  }, [hasMore, loading, handleLoadMore, searchActive]);

  // Debounced search effect
  useEffect(() => {
    // Don't trigger search on initial mount when searchQuery is empty
    if (searchQuery === "") {
      setSearchActive(false);
      return;
    }

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    debounceTimeout.current = setTimeout(() => {
      const query = searchQuery.trim();
      setSearchActive(!!query);
    }, 300);

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [searchQuery]);

  const handleClearSearch = () => {
    setSearchQuery("");
    setSearchActive(false);
  };

  // Filter reports based on search query (and optionally by deleted status)
  const getFilteredReports = () => {
    if (!searchQuery.trim()) {
      return reports;
    }

    const query = searchQuery.toLowerCase();
    return reports.filter((report) => {
      const driverName = (report.event_driver_name || "").toLowerCase();
      const driverId = (report.event_driver_id || "").toLowerCase();
      const carLicense = (report.insured_car_license || "").toLowerCase();
      const company = (report.selected_company || "").toLowerCase();
      const email = (report.email_to_send_pdf || "").toLowerCase();
      const accidentDate = (report.acident_date || "").toLowerCase();
      const damageDesc = (report.insured_damage_description || "").toLowerCase();
      const accidentDesc = (report.acident_description || "").toLowerCase();
      const createdAt = formatDate(report.createdAt).toLowerCase();

      return (
        driverName.includes(query) ||
        driverId.includes(query) ||
        carLicense.includes(query) ||
        company.includes(query) ||
        email.includes(query) ||
        accidentDate.includes(query) ||
        damageDesc.includes(query) ||
        accidentDesc.includes(query) ||
        createdAt.includes(query)
      );
    });
  };

  // Sort filtered reports (client-side sorting on loaded data)
  const getSortedFilteredReports = () => {
    const filtered = getFilteredReports();
    return [...filtered].sort((a, b) => {
      let cmp: number;
      if (sortBy === "selected_company") {
        const aVal = (a.selected_company || "").toLowerCase();
        const bVal = (b.selected_company || "").toLowerCase();
        cmp = aVal.localeCompare(bVal);
      } else {
        const aTime = new Date(a.createdAt).getTime();
        const bTime = new Date(b.createdAt).getTime();
        cmp = aTime - bTime;
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
  };

  // Render table rows
  const renderTableRows = () => {
    const filteredReports = getSortedFilteredReports();
    return filteredReports.map((report, index) => {
      const originalIndex = reports.findIndex((r) => r._id === report._id);
      const isDeleted = !!report.deleted;
      return (
        <Tr
          key={report._id}
          className={styles.TrData + (isDeleted ? " " + styles.TrDataDeleted : "")}
        >
          {isAuthenticated && (
            <Td className={styles.SelectCell + " " + styles.SelectHeader}>
              <Checkbox
                name={`select-report-${report._id}`}
                checked={selectedReports.includes(report._id)}
                onChange={(e) =>
                  toggleSelectReport(
                    report,
                    originalIndex >= 0 ? originalIndex : index,
                    (e.nativeEvent as MouseEvent).shiftKey
                  )
                }
                size="small"
                className={styles.TableCheckbox}
                ariaLabel={`בחר ${report.file_name || "דוח"}`}
              />
            </Td>
          )}
          <Td className={styles.DriverNameContainer} data-label="שם הנהג">
            {report.event_driver_name || "-"}
          </Td>
          <Td className={styles.DriverIdContainer} data-label="תעודת זהות נהג">
            {report.event_driver_id || "-"}
          </Td>
          <Td className={styles.CarLicenseContainer} data-label="מספר רכב מבוטח">
            {report.insured_car_license || "-"}
          </Td>
          <Td className={styles.CompanyContainer} data-label="חברה נבחרת">
            {report.selected_company || "-"}
          </Td>
          <Td className={styles.EmailContainer} data-label="אימייל">
            {report.email_to_send_pdf || "-"}
          </Td>
          <Td className={styles.AccidentDateContainer} data-label="תאריך תאונה">
            {report.acident_date ? formatDate(report.acident_date) : "-"}
          </Td>
          <Td className={styles.DamageDescriptionContainer} data-label="תיאור הנזק">
            <div className={styles.NotesContent}>{report.insured_damage_description || "-"}</div>
          </Td>
          <Td className={styles.AccidentDescriptionContainer} data-label="תיאור המקרה">
            <div className={styles.NotesContent}>{report.acident_description || "-"}</div>
          </Td>
          <Td className={styles.DateContainer} data-label="תאריך יצירה">
            {formatDate(report.createdAt)}
          </Td>
          <Td className={styles.DownloadContainer} data-label="הורדה">
            <div
              onClick={() => handleDownload(report.file_url, report.file_name)}
              className={styles.DownloadButton}
              title="הורד קובץ"
            >
              <DownloadIcon />
            </div>
          </Td>
          {isAdminUser && (
            <Td className={styles.EditContainer} data-label="עריכה">
              {!isDeleted && (
                <div
                  onClick={() => setEditReport(report)}
                  className={styles.EditButton}
                  title="ערוך שורה"
                >
                  <EditIcon size={18} />
                </div>
              )}
            </Td>
          )}
          {isAdminUser && isDeleted && (
            <Td className={styles.RemoveContainer} data-label="מחק לצמיתות">
              <Button
                label="מחק לצמיתות"
                color="outlineAccent"
                size="small"
                width="auto"
                onClick={() => showHardDeleteConfirm(report._id, report.file_name)}
              />
            </Td>
          )}
          {isAuthenticated && !isDeleted && canDeleteReports && (
            <Td className={styles.RemoveContainer} data-label="מחק">
              <div
                onClick={() =>
                  showDeleteConfirm(report._id, report.file_name)
                }
                className={styles.RowDeleteButton}
              >
                <div className={styles.Delicon}>
                  <DeleteIcon />
                </div>
              </div>
            </Td>
          )}
        </Tr>
      );
    });
  };

  // Hard delete confirmation dialog
  const HardDeleteConfirmDialog = hardDeleteConfirm.show ? (
    <div className={styles.ConfirmDelete}>
      <div className={styles.TitleDelete} style={tokens.typography.h6}>
        {hardDeleteConfirm.isBulk
          ? `למחוק לצמיתות ${hardDeleteConfirm.ids.length} דוחות תאונות שנבחרו?`
          : `למחוק לצמיתות דוח תאונה "${hardDeleteConfirm.fileName}"?`}
      </div>
      <div className={styles.HardDeleteWarning}>פעולה זו בלתי הפיכה!</div>
      <div className={styles.BtContainer}>
        <Button
          label={hardDeleting ? "מוחק..." : "מחק לצמיתות"}
          color="primary"
          size="medium"
          width={140}
          onClick={confirmHardDelete}
          disabled={hardDeleting}
        />
        <Button
          label="ביטול"
          color="outlineSecondary"
          size="medium"
          width={120}
          onClick={() =>
            setHardDeleteConfirm({ show: false, ids: [], fileName: "", isBulk: false })
          }
        />
      </div>
    </div>
  ) : null;

  // Delete confirmation dialog
  const DeleteConfirmDialog = deleteConfirm.show ? (
    <div className={styles.ConfirmDelete}>
      <div className={styles.TitleDelete} style={tokens.typography.h6}>
        {deleteConfirm.isBulk
          ? `למחוק ${deleteConfirm.ids.length} דוחות תאונות שנבחרו?`
          : `למחוק דוח תאונה "${deleteConfirm.fileName}"?`}
      </div>
      <div className={styles.BtContainer}>
        <Button
          label={bulkDeleting ? "מוחק..." : "מחק"}
          color="primary"
          size="medium"
          width={120}
          onClick={confirmDelete}
          disabled={bulkDeleting}
        />
        <Button
          label="ביטול"
          color="outlineSecondary"
          size="medium"
          width={120}
          onClick={cancelDelete}
        />
      </div>
    </div>
  ) : null;

  // Empty state
  const filteredReports = getFilteredReports();
  const EmptyState =
    !loading && reports.length === 0 ? (
      <div className={styles.EmptyState}>אין דוחות תאונות להצגה</div>
    ) : searchActive && filteredReports.length === 0 ? (
      <div className={styles.EmptyState}>לא נמצאו תוצאות לחיפוש</div>
    ) : null;

  const noBulkSelection = !selectedReports.length;
  const bulkDropdownItems: DropdownItem[] = [
    {
      id: "download",
      label: bulkDownloading ? "מוריד..." : "הורד נבחרים (ZIP)",
      onClick: handleDownloadSelected,
      disabled: noBulkSelection || loading || bulkDownloading,
    },
    ...(canDeleteReports
      ? [
          showDeleted
            ? {
                id: "hard-delete",
                label: hardDeleting ? "מוחק..." : "מחק נבחרים לצמיתות",
                onClick: handleHardDeleteSelected,
                disabled: noBulkSelection || hardDeleting || loading,
              }
            : {
                id: "delete",
                label: bulkDeleting ? "מוחק..." : "מחק נבחרים",
                onClick: handleDeleteSelected,
                disabled: noBulkSelection || bulkDeleting || loading,
              },
        ]
      : []),
  ];

return (
    <div className={styles.Container} dir="rtl">
      <div className={styles.HeaderContainer}>
        <div className={styles.Title} style={tokens.typography.h2}>
          טבלת דוחות תאונות
        </div>
        {isAdminUser && (
          <Checkbox
            name="showDeleted"
            title="הצג פריטים שנמחקו"
            checked={showDeleted}
            onChange={(e) => setShowDeleted(e.target.checked)}
            className={styles.ShowDeletedToggle}
            size="large"
          />
        )}
        <SearchInput
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          searchActive={searchActive}
          handleClearSearch={handleClearSearch}
          searchInputRef={searchInputRef}
        />
      </div>
      {DeleteConfirmDialog}
      {HardDeleteConfirmDialog}
      <EditAccidentReportModal
        report={editReport}
        open={!!editReport}
        onClose={() => setEditReport(null)}
        onSaved={handleEditSaved}
      />
      {loading && reports.length === 0 ? (
        <div className={styles.LoadingContainer}>
          <MoonLoader size={50} color={tokens.colors.secondary} />
        </div>
      ) : error ? (
        <div className={styles.ErrorState}>
          <ErrorMessage message={error} size="medium" />
          <Button
            label="נסה שוב"
            color="secondary"
            size="medium"
            width={120}
            onClick={() => loadReports(1, false)}
          />
        </div>
      ) : (
        <>
          {EmptyState}
          {(reports.length > 0 || searchActive) && (
            <div className={styles.TableWrapper}>
              <div className={styles.TableInner}>
              <div className={styles.BulkActions}>
                {isAuthenticated && (
                  <div className={styles.BulkActionsGroup}>
                    <Dropdown
                      triggerLabel="פעולות"
                      triggerColor="outlineSecondary"
                      triggerSize="medium"
                      items={bulkDropdownItems}
                      ariaLabel="פעולות על נבחרים"
                    />
                    {selectedReports.length ? (
                      <span className={styles.SelectedCount}>
                        {`${selectedReports.length} נבחרו`}
                      </span>
                    ) : null}
                  </div>
                )}
                <div className={styles.LinkContainer}>
                  <a
                    href="https://rafael-car-show.co.il/accident-report"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      ...tokens.typography.body,
                      color: tokens.colors.text.secondary,
                      textDecoration: "underline",
                    }}
                  >
                    לעמוד הטופס
                  </a>
                </div>
              </div>
              <div className={styles.TableFrame}>
              <div
                ref={tableScrollProps.ref}
                className={`${styles.TableScroll} ${tableScrollProps.isDraggingScroll ? styles.TableScrollDragging : ""}`}
                onMouseDown={tableScrollProps.onMouseDown}
                style={tableScrollProps.style}
              >
              <Table className={styles.Table}>
                <Thead>
                  <Tr className={styles.TrHeader}>
                    {isAuthenticated && (
                      <Th className={`${styles.ThTitle} ${styles.SelectHeader}`}>
                        <Checkbox
                          name="select-all-reports"
                          checked={
                            filteredReports.length > 0 &&
                            selectedReports.length === filteredReports.length &&
                            filteredReports.every((r) =>
                              selectedReports.includes(r._id)
                            )
                          }
                          semiSelected={
                            selectedReports.length > 0 &&
                            selectedReports.length < filteredReports.length
                          }
                          onChange={(e) => toggleSelectAll(e.target.checked)}
                          size="small"
                          color="onPrimary"
                          className={`${styles.TableCheckbox} ${styles.hideOnMobile}`}
                          ariaLabel="בחר הכל"
                        />
                      </Th>
                    )}
                    <Th className={styles.ThTitle}>שם הנהג</Th>
                    <Th className={styles.ThTitle}>תעודת זהות נהג</Th>
                    <Th className={styles.ThTitle}>מספר רכב מבוטח</Th>
                    <Th
                      className={`${styles.ThTitle} ${styles.ThTitleSortable}`}
                      style={{ cursor: "pointer", userSelect: "none" }}
                      onClick={toggleSortByCompany}
                      aria-sort={
                        sortBy === "selected_company"
                          ? sortDir === "asc"
                            ? "ascending"
                            : "descending"
                          : undefined
                      }
                    >
                      חברה נבחרת
                      <span style={{ marginRight: 4, fontSize: 10, opacity: sortBy === "selected_company" ? 1 : 0.4 }}>
                        {sortBy === "selected_company" ? (sortDir === "asc" ? " ▲" : " ▼") : "▼"}
                      </span>
                    </Th>
                    <Th className={styles.ThTitle}>אימייל</Th>
                    <Th className={styles.ThTitle}>תאריך תאונה</Th>
                    <Th className={styles.ThTitle}>תיאור הנזק</Th>
                    <Th className={styles.ThTitle}>תיאור המקרה</Th>
                    <Th
                      className={`${styles.ThTitle} ${styles.ThTitleSortable}`}
                      style={{ cursor: "pointer", userSelect: "none" }}
                      onClick={toggleSortByCreatedAt}
                      aria-sort={
                        sortBy === "createdAt"
                          ? sortDir === "asc"
                            ? "ascending"
                            : "descending"
                          : undefined
                      }
                    >
                      תאריך יצירה
                      <span style={{ marginRight: 4, fontSize: 10, opacity: sortBy === "createdAt" ? 1 : 0.4 }}>
                        {sortBy === "createdAt" ? (sortDir === "asc" ? " ▲" : " ▼") : "▼"}
                      </span>
                    </Th>
                    <Th className={styles.ThTitle}>הורדה</Th>
                    {isAdminUser && <Th className={styles.ThTitle}>עריכה</Th>}
                    {canDeleteReports && <Th className={styles.ThTitle}>מחק</Th>}
                  </Tr>
                </Thead>
                <Tbody>
                  {renderTableRows()}
                  {loading && hasMore && (
                    <Tr>
                      <Td colSpan={10 + (isAuthenticated ? 1 : 0) + (isAdminUser ? 1 : 0) + (canDeleteReports ? 1 : 0)} className={styles.LoadingMore}>
                        טוען עוד...
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
              </div>
              </div>
              {reports.length > 10 && hasMore && !searchActive && (
                <div ref={loadMoreRef} className={styles.LoadMoreTrigger} />
              )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AccidentReportTable;

